import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Crown, AlertTriangle, Clock, Loader2 } from "lucide-react";
import api, { apiError } from "../../lib/api";
import { fmtDate, fmtDateTime } from "../../lib/time";
import { useAuth } from "../../context/AuthContext";
import { Label } from "../../components/common";
import { Button } from "../../components/ui/button";

export default function Billing() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState("");
  const isCorp = user?.role === "corporate_admin";

  const load = () => api.get("/billing").then((r) => setD(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const requestPlan = async (planId, name) => {
    setBusy(planId);
    try {
      await api.post("/billing/request", { plan_id: planId });
      toast.success(`Request sent for the ${name} plan — Servantage will be in touch`);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(""); }
  };

  if (!d) return <div className="text-slate-400">Loading billing…</div>;
  const s = d.subscription;
  const cur = (n) => `R${Number(n || 0).toLocaleString()}`;
  const capTxt = (used, max) => max == null ? `${used} / Unlimited` : `${used} / ${max}`;

  return (
    <div className="space-y-6" data-testid="billing-page">
      <div>
        <Label>Plan & Billing</Label>
        <h1 className="font-head text-2xl font-bold text-slate-900 sm:text-3xl">Your subscription</h1>
      </div>

      {d.org_status === "suspended" && (
        <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700" data-testid="billing-suspended">
          <AlertTriangle className="h-5 w-5" /> Your organisation is suspended for non-payment. Settle your invoice to restore access.
        </div>
      )}
      {s.overdue && d.org_status !== "suspended" && (
        <div className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm font-semibold text-amber-800" data-testid="billing-overdue">
          <Clock className="h-5 w-5" /> Payment overdue since {fmtDate(s.paid_until)}. Please settle to avoid suspension.
        </div>
      )}
      {s.due_soon && (
        <div className="flex items-center gap-2 rounded-xl border border-orange-200 bg-orange-50 p-4 text-sm font-semibold text-orange-800" data-testid="billing-due-soon">
          <Clock className="h-5 w-5" /> Your next payment is due {s.days_left === 0 ? "today" : s.days_left === 1 ? "tomorrow" : `in ${s.days_left} days`} ({fmtDate(s.paid_until)}). Please arrange payment to keep access uninterrupted.
        </div>
      )}

      {s.awaiting_payment && (
        <div className="flex items-center gap-2 rounded-xl border border-indigo-200 bg-indigo-50 p-4 text-sm font-semibold text-indigo-700" data-testid="billing-awaiting">
          <Clock className="h-5 w-5" /> Your {s.plan_name} plan is awaiting first payment. We'll activate it once received.
        </div>
      )}
      {d.pending_request && (
        <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-4 text-sm text-indigo-700" data-testid="billing-pending-request">
          Request pending: <b>{d.pending_request.requested_plan_name}</b> — Servantage is processing it.
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="billing-current">
          <Label>Current plan</Label>
          <div className="mt-1 flex items-center gap-2">
            <Crown className="h-5 w-5 text-indigo-600" />
            <span className="font-head text-2xl font-bold text-slate-900">{s.plan_name}</span>
            <span className="text-sm text-slate-400">{s.price ? `${cur(s.price)}/mo` : "Free"}</span>
          </div>
          {s.paid_until && <p className="mt-2 text-xs text-slate-500">Paid until {fmtDate(s.paid_until)}</p>}
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="billing-usage">
          <Label>Usage</Label>
          <div className="mt-2 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">Locations</span><span className="font-bold">{capTxt(d.usage.locations, s.max_locations)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Active employees</span><span className="font-bold">{capTxt(d.usage.employees, s.max_employees)}</span></div>
          </div>
        </div>
      </div>

      <div>
        <Label>Available plans</Label>
        <div className="mt-2 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {d.plans.map((p) => {
            const current = p.id === s.plan_id;
            return (
              <div key={p.id} data-testid={`plan-card-${p.id}`}
                className={`flex flex-col rounded-xl border p-5 ${current ? "border-indigo-400 ring-1 ring-indigo-200" : "border-slate-200"}`}>
                <h3 className="font-head text-lg font-bold text-slate-900">{p.name}</h3>
                <div className="mt-1 text-2xl font-bold text-indigo-700">{p.price ? cur(p.price) : "Free"}<span className="text-sm font-normal text-slate-400">{p.price ? "/mo" : ""}</span></div>
                <ul className="mt-3 flex-1 space-y-1.5 text-sm text-slate-600">
                  {(p.features || []).map((f, i) => <li key={i} className="flex items-start gap-1.5"><Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />{f}</li>)}
                </ul>
                {current ? (
                  <div className="mt-4 rounded-full bg-indigo-50 py-2 text-center text-sm font-bold text-indigo-700" data-testid={`plan-current-${p.id}`}>Current plan</div>
                ) : isCorp && p.price > 0 ? (
                  <Button onClick={() => requestPlan(p.id, p.name)} disabled={!!busy} data-testid={`plan-request-${p.id}`}
                    className="mt-4 w-full rounded-full bg-[#4338CA] hover:bg-[#3730A3]">
                    {busy === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : "Request this plan"}
                  </Button>
                ) : <div className="mt-4 h-9" />}
              </div>
            );
          })}
        </div>
        {!isCorp && <p className="mt-2 text-xs text-slate-400">Only the corporate admin can request a plan change.</p>}
      </div>

      {d.payments.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="billing-payments">
          <Label>Payment history</Label>
          <table className="mt-2 w-full text-sm">
            <thead className="text-left text-xs font-semibold uppercase tracking-wide text-slate-400">
              <tr><th className="py-1">Date</th><th>Plan</th><th>Amount</th><th>Period</th></tr>
            </thead>
            <tbody>
              {d.payments.map((p) => (
                <tr key={p.id} className="border-t border-slate-100">
                  <td className="py-1.5">{fmtDateTime(p.created_at)}</td><td>{p.plan_name}</td>
                  <td className="font-semibold">{cur(p.amount)}</td>
                  <td className="text-slate-500">{fmtDate(p.period_start)} → {fmtDate(p.period_end)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
